// FRT Customer detail page — name + code edit form, linked accounts embed,
// suppress/restore controls (admin only).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { accounts, customers, lookups } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { HardDeleteButton } from '@/components/HardDeleteButton';
import type { HardDeleteMode } from '@frt/shared-types';
import { PlatformsCard } from './platforms-card';

export function CustomerDetailPage() {
  const { id: idStr } = useParams({ strict: false }) as { id?: string };
  const id = Number(idStr);
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const canEdit = me?.role === 'admin' || me?.role === 'senior';
  const canSuppress = me?.role === 'admin';

  const { data: customer, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customers.get(id),
    enabled: Number.isFinite(id),
  });
  const { data: accountsList } = useQuery({
    queryKey: ['accounts-by-customer', id],
    queryFn: () => accounts.list({ frt_customer_id: id, per_page: 200 }),
    enabled: Number.isFinite(id),
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  useEffect(() => {
    if (customer) {
      setCode(customer.code);
      setName(customer.name);
      setNotes(customer.notes ?? '');
      setCategoryId(customer.frt_category_id ?? '');
    }
  }, [customer]);

  const { data: categories } = useQuery({
    queryKey: ['lookups', 'frt-categories'],
    queryFn: () => lookups.frtCategories.list(),
  });

  const update = useMutation({
    mutationFn: () =>
      customers.update(id, {
        code,
        name,
        notes: notes || null,
        ...(categoryId ? { frt_category_id: Number(categoryId) } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer', id] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts-by-customer', id] });
    },
  });
  const suppress = useMutation({
    mutationFn: () => customers.suppress(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer', id] }),
  });
  const restore = useMutation({
    mutationFn: () => customers.restore(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customer', id] }),
  });
  const navigate = useNavigate();
  const deleteCustomer = useMutation({
    mutationFn: (body: { mode: HardDeleteMode; reason: string | null }) =>
      customers.remove(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      void navigate({ to: '/customers' });
    },
  });

  if (!Number.isFinite(id)) return <div className="text-sm text-red-600">Invalid id</div>;
  if (isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (error) return <div className="text-sm text-red-600">{(error as Error).message}</div>;
  if (!customer) return <div className="text-sm text-slate-500">Not found</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">
          {customer.name}{' '}
          <span className="text-sm text-slate-500 font-normal">({customer.code})</span>
        </h1>
        <div className="flex items-center gap-2">
          {canSuppress && customer.status === 'active' && (
            <Button
              variant="destructive"
              onClick={() => {
                if (confirm('Suppress this customer? Linked accounts are unaffected.')) {
                  suppress.mutate();
                }
              }}
            >
              Suppress
            </Button>
          )}
          {canSuppress && customer.status === 'suppressed' && (
            <Button onClick={() => restore.mutate()}>Restore</Button>
          )}
          {me?.role === 'admin' && (
            <HardDeleteButton
              entityLabel="customer"
              onConfirm={(body) => deleteCustomer.mutate(body)}
            />
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Customer details</CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!canEdit) return;
              if (
                code !== customer.code &&
                !confirm(
                  `Renaming the code will recompute display_name on all ${customer.account_count} linked accounts. Continue?`,
                )
              ) {
                return;
              }
              update.mutate();
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block text-sm">
                <span className="block text-slate-600">Code</span>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={!canEdit}
                />
              </label>
              <label className="block text-sm">
                <span className="block text-slate-600">Name</span>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEdit}
                />
              </label>
              <label className="block text-sm">
                <span className="block text-slate-600">FRT Category</span>
                <select
                  value={categoryId}
                  onChange={(e) =>
                    setCategoryId(e.target.value ? Number(e.target.value) : '')
                  }
                  disabled={!canEdit}
                  className="block w-full rounded border border-slate-300 bg-white px-2 py-2 text-sm"
                >
                  <option value="">—</option>
                  {(categories?.data ?? []).map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block text-sm">
              <span className="block text-slate-600">Notes</span>
              <textarea
                className="block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={!canEdit}
                rows={3}
              />
            </label>
            {canEdit && (
              <div>
                <Button type="submit" disabled={update.isPending}>
                  Save
                </Button>
                {update.error && (
                  <span className="ml-3 text-sm text-red-600">
                    {(update.error as Error).message}
                  </span>
                )}
                {update.data?.cascade_renames !== undefined && (
                  <span className="ml-3 text-sm text-slate-600">
                    Renamed {update.data.cascade_renames} account(s).
                  </span>
                )}
              </div>
            )}
          </form>
        </CardBody>
      </Card>

      {customer.is_customer !== false && <PlatformsCard customerId={id} canEdit={canEdit} />}

      <Card>
        <CardHeader>
          <CardTitle>
            Linked accounts ({customer.account_count})
          </CardTitle>
        </CardHeader>
        <CardBody>
          {!accountsList ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : accountsList.data.length === 0 ? (
            <div className="text-sm text-slate-500">No accounts linked.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-1 pr-3">Display name</th>
                  <th className="py-1 pr-3">Tester</th>
                  <th className="py-1 pr-3">Tiller</th>
                  <th className="py-1 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {accountsList.data.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="py-1 pr-3">
                      <Link
                        to="/accounts/$id"
                        params={{ id: String(a.id) }}
                        className="text-brand-700 hover:underline font-mono"
                      >
                        {a.display_name}
                      </Link>
                    </td>
                    <td className="py-1 pr-3">{a.tester_display}</td>
                    <td className="py-1 pr-3">{a.tiller_status}</td>
                    <td className="py-1 pr-3">{a.account_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
